import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// Editable email templates API — mirrors backend/src/routes/emailTemplates.ts.
// Templates live in code server-side; the DB stores per-key overrides, so GET
// always returns a resolved template (override or default + isCustomized),
// PUT saves the override and DELETE resets to the code default.
// ---------------------------------------------------------------------------

export type EmailTemplateKey = 'overdue_invoice' | 'welcome';

export interface EmailTemplateVariable {
  name: string;
  /** True when the variable is injected as raw pre-rendered HTML. */
  html?: boolean;
}

export interface EmailTemplate {
  key: EmailTemplateKey;
  subject: string;
  bodyHtml: string;
  variables: EmailTemplateVariable[];
  isCustomized: boolean;
  updatedAt: string | null;
}

export interface EmailTemplateInput {
  subject: string;
  bodyHtml: string;
}

export interface EmailTemplatePreview {
  subject: string;
  /** Full branded document for an <iframe srcDoc> preview. */
  html: string;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** GET /api/email-templates — every catalog template, resolved. */
export async function listEmailTemplates(): Promise<EmailTemplate[]> {
  const res = await apiFetch<Envelope<EmailTemplate[]>>('/api/email-templates', {
    headers: authHeaders(),
  });
  return res.data;
}

/** PUT /api/email-templates/:key — save the override. */
export async function saveEmailTemplate(
  key: EmailTemplateKey,
  input: EmailTemplateInput,
): Promise<EmailTemplate> {
  const res = await apiFetch<Envelope<EmailTemplate>>(`/api/email-templates/${key}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/** DELETE /api/email-templates/:key — reset to the code default. */
export async function resetEmailTemplate(key: EmailTemplateKey): Promise<EmailTemplate> {
  const res = await apiFetch<Envelope<EmailTemplate>>(`/api/email-templates/${key}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}

/** POST /api/email-templates/:key/preview — render the draft with sample data. */
export async function previewEmailTemplate(
  key: EmailTemplateKey,
  input: EmailTemplateInput,
): Promise<EmailTemplatePreview> {
  const res = await apiFetch<Envelope<EmailTemplatePreview>>(
    `/api/email-templates/${key}/preview`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(input),
    },
  );
  return res.data;
}
